import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  InputAdornment,
  TextFieldProps,
  CircularProgress,
  Box,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTranslate } from "src/locales";
import ByzMuiTextField from "./ByzMuiTextField";
import Tooltip from "@mui/material/Tooltip";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiDataGrid from "../data-grid";
import { baseService } from "src/api/core/baseService";
import { MutationResponse } from "src/modules/nurse/patient-list/pages/list/models";
import { GridToolbar, GridToolbarContainer, GridToolbarQuickFilter } from "@mui/x-data-grid-premium";
import { GridRowSelectionModel, useGridApiRef, DataGridPremium } from "@mui/x-data-grid-premium";

function CustomToolbar() {
  const { t } = useTranslate();
  return (
    <GridToolbarContainer sx={{ color: "#00B8D9" }}>
      <GridToolbar />
      <GridToolbarQuickFilter placeholder={t("search")} sx={{ ml: "auto" }} />
    </GridToolbarContainer>
  );
}

export type ByzSelectListPopupInputProps = TextFieldProps & {
  url?: string;
  method?: "GET" | "POST";
  postData?: any;
  label?: string;
  dialogTitle?: string;
  dialogShowOkButton?: boolean;
  onDialogOkClick?: () => void;
  onChange?: (value: any) => void;
  dialogOpen?: boolean;
  setDialogOpen?: (open: boolean) => void;
  displayField?: string;
  columns?: any[];
  /** Hasta kartı için id (örn. patientId) */
  rowID?: any;
  /** Hasta kartı gösterilsin mi (varsayılan rowID varsa true) */
  showPatientData?: boolean;
  /** Seçili öğe (başlangıçta seçili gösterilecek) */
  selectedItem?: any;
  /** Tek seçim modu (varsayılan: false - çoklu seçim) */
  singleSelection?: boolean;
  // Opsiyonel: dışarıdan hazır liste verisi verildiğinde API çağrısı yapmadan bunu kullan
  staticData?: any[];
  // Opsiyonel: popup'ta hasta kartı gösterilsin mi? (varsayılan: false)
  dialogShowPatientData?: boolean;
  // Opsiyonel: popup yüksekliği (ör: "60vh")
  dialogMaxHeight?: string | number;
  // Opsiyonel: seçili ID bilgisi; modal açıldığında grid'de seçili göstermek için
  selectedId?: string | string[];
};

const ByzSelectListPopupInput: React.FC<ByzSelectListPopupInputProps> = ({
  url,
  method = "GET",
  postData,
  label,
  dialogTitle,
  dialogShowOkButton = true,
  onDialogOkClick,
  onChange,
  dialogOpen,
  setDialogOpen,
  displayField = "NAME",
  columns,
  rowID,
  showPatientData,
  selectedItem,
  singleSelection = true,
  staticData,
  dialogShowPatientData = false,
  dialogMaxHeight,
  selectedId,
  ...rest
}) => {
  const { t } = useTranslate();
  const apiRef = useGridApiRef();
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Dışarıdan gelen value (çoğunlukla label metni)
  const externalValue = (rest as { value?: any }).value;
  const [inputValue, setInputValue] = useState<any>(externalValue || "");
  const [data, setData] = useState<any[]>([]);
  const [selectionModel, setSelectionModel] = useState<string[]>([]);
  const latestSelectionRef = useRef<string[]>([]);

  const idsFromSelectedId = React.useMemo(() => {
    if (!selectedId) return [] as string[];
    if (Array.isArray(selectedId)) return selectedId.map((id) => String(id));
    return [String(selectedId)];
  }, [selectedId]);

  const handleSelectionChange = (ids: GridRowSelectionModel) => {
    // Tek seçim modunda sadece son seçilen item'ı tut
    if (singleSelection) {
      const lastId = ids.length > 0 ? String(ids[ids.length - 1]) : "";
      latestSelectionRef.current = lastId ? [lastId] : [];
      setSelectionModel(lastId ? [lastId] : []);
    } else {
      const stringIds = ids.map(id => String(id));
      latestSelectionRef.current = stringIds;
      setSelectionModel(stringIds);
    }
  };

  // Dışarıdan value değiştiğinde input ve seçim modelini senkronize et
  useEffect(() => {
    setInputValue(externalValue || "");
    // Değer temizlendiyse grid seçimini de sıfırla
    if (!externalValue) {
      setSelectionModel([]);
      latestSelectionRef.current = [];
    }
  }, [externalValue]);

  // Dışarıdan selectedId değiştiğinde grid seçimini senkronize et
  useEffect(() => {
    if (!selectedId) {
      setSelectionModel([]);
      latestSelectionRef.current = [];
      return;
    }
    const ids = idsFromSelectedId;
    setSelectionModel(ids);
    latestSelectionRef.current = ids;
  }, [selectedId, idsFromSelectedId]);

  // value prop'u veya selectedItem değiştiğinde inputValue'yu güncelle
  useEffect(() => {
    const valueProp = (rest as { value?: any }).value;
    // Eğer value prop'u varsa onu kullan, yoksa selectedItem'dan türet
    if (valueProp !== undefined && valueProp !== null && valueProp !== "") {
      setInputValue(valueProp);
    } else if (selectedItem) {
      if (Array.isArray(selectedItem)) {
        // Çoklu seçim - tüm isimleri birleştir
        const displayValues = selectedItem
          .map((item: any) => {
            // Tüm olası field adlarını kontrol et
            return item?.[displayField] || item?.name || item?.NAME || item?.Name || item?.displayName || "";
          })
          .filter((val: string) => val);
        if (displayValues.length > 0) {
          setInputValue(displayValues.join(", "));
        } else {
          setInputValue("");
        }
      } else {
        // Tek seçim - tüm olası field adlarını kontrol et
        const displayValue = selectedItem?.[displayField] || selectedItem?.name || selectedItem?.NAME || selectedItem?.Name || selectedItem?.displayName || "";
        setInputValue(displayValue);
      }
    } else {
      setInputValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(rest as { value?: any }).value, selectedItem, displayField]);

  const isOpen = dialogOpen !== undefined ? dialogOpen : internalOpen;

  const handleOpen = () => {
    // Disabled ise açma
    if (rest.disabled) return;
    // selectedId varsa onu kullan, yoksa selectedItem'a bak
    if (selectedId) {
      setSelectionModel(idsFromSelectedId);
      latestSelectionRef.current = idsFromSelectedId;
    } else if (!selectedItem) {
      // selectedItem yoksa seçimi temizle
      setSelectionModel([]);
      latestSelectionRef.current = [];
    }
    // selectedItem varsa seçim useEffect içinde data yüklendikten sonra güncellenecek
    if (setDialogOpen) setDialogOpen(true);
    else setInternalOpen(true);
  };

  const handleClose = () => {
    if (setDialogOpen) setDialogOpen(false);
    else setInternalOpen(false);
  };

  const defaultColumns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "NAME", headerName: "Adı", flex: 1 },
  ];
  const dataColumns = columns ?? defaultColumns;

  // Dummy data generator based on URL
  const getDummyData = (): any[] => {
    if (!url) return [];
    if (url.includes("GetDiseaseList")) {
      return [
        { ID: 1, NAME: "Diyabet", id: "1" },
        { ID: 2, NAME: "Hipertansiyon", id: "2" },
        { ID: 3, NAME: "Astım", id: "3" },
        { ID: 4, NAME: "KOAH", id: "4" },
        { ID: 5, NAME: "Koroner Arter Hastalığı", id: "5" },
      ];
    }
    if (url.includes("GetSpirometryList")) {
      return [
        { ID: 1, NAME: "FEV1 ≥ %80", id: "1" },
        { ID: 2, NAME: "FEV1 %60–80", id: "2" },
        { ID: 3, NAME: "FEV1 %40–60", id: "3" },
        { ID: 4, NAME: "FEV1 < %40", id: "4" },
      ];
    }
    if (url.includes("GetHabitList")) {
      return [
        { ID: 1, NAME: "Sigara", id: "1" },
        { ID: 2, NAME: "Alkol", id: "2" },
        { ID: 3, NAME: "Nargile", id: "3" },
        { ID: 4, NAME: "Diğer", id: "4" },
      ];
    }
    if (url.includes("GetAllergenList")) {
      return [
        { ID: 1, NAME: "Yumurta", id: "1" },
        { ID: 2, NAME: "Fıstık", id: "2" },
        { ID: 3, NAME: "Süt", id: "3" },
        { ID: 4, NAME: "Penisilin", id: "4" },
      ];
    }
    if (url.includes("GetOtherAllergenList")) {
      return [
        { ID: 1, NAME: "Ev tozu", id: "1" },
        { ID: 2, NAME: "Polen", id: "2" },
        { ID: 3, NAME: "Hayvan tüyü", id: "3" },
      ];
    }
    if (url.includes("GetSurgeryList")) {
      return [
        { ID: 1, NAME: "Abbe", id: "1" },
        { ID: 2, NAME: "Appendektomi", id: "2" },
        { ID: 3, NAME: "Kolesistektomi", id: "3" },
        { ID: 4, NAME: "CABG", id: "4" },
        { ID: 5, NAME: "Tiroidektomi", id: "5" },
      ];
    }
    if (url.includes("GetFamilyRelationList")) {
      return [
        { ID: 1, NAME: "Anne", id: "1" },
        { ID: 2, NAME: "Baba", id: "2" },
        { ID: 3, NAME: "Kardeş", id: "3" },
        { ID: 4, NAME: "Dede", id: "4" },
        { ID: 5, NAME: "Nine", id: "5" },
      ];
    }
    if (url.includes("GetDrugList")) {
      return [
        { ID: 1, NAME: "Parol", id: "1" },
        { ID: 2, NAME: "Metformin", id: "2" },
        { ID: 3, NAME: "Aspirin", id: "3" },
        { ID: 4, NAME: "Diğer", id: "4" },
      ];
    }
    if (url.includes("GetExposureTypeList")) {
      return [
        { ID: 1, NAME: "Çevresel", id: "1" },
        { ID: 2, NAME: "Mesleki", id: "2" },
        { ID: 3, NAME: "Radyasyon", id: "3" },
        { ID: 4, NAME: "Kimyasal", id: "4" },
        { ID: 5, NAME: "Diğer", id: "5" },
      ];
    }
    if (url.includes("field-exposure")) {
      return [
        { id: "0", name: "Mesleki Maruziyet", ID: 0, NAME: "Mesleki Maruziyet" },
        { id: "1", name: "Çevresel Maruziyet", ID: 1, NAME: "Çevresel Maruziyet" },
      ];
    }
    if (url.includes("GetSpecialNoteList")) {
      return [
        { ID: 1, NAME: "Özel Not", id: "1" },
        { ID: 2, NAME: "Önemli Bilgi", id: "2" },
        { ID: 3, NAME: "Uyarı", id: "3" },
        { ID: 4, NAME: "Hatırlatma", id: "4" },
        { ID: 5, NAME: "Diğer", id: "5" },
      ];
    }
    if (url.includes("GetRareDiseaseList")) {
      return [
        { ID: 1, NAME: "Huntington Hastalığı", id: "1" },
        { ID: 2, NAME: "Amyotrofik Lateral Skleroz (ALS)", id: "2" },
        { ID: 3, NAME: "Kas Distrofisi", id: "3" },
        { ID: 4, NAME: "Kistik Fibrozis", id: "4" },
        { ID: 5, NAME: "Diğer", id: "5" },
      ];
    }
    return [];
  };

  useEffect(() => {
    if (!isOpen) return;

    // Eğer bu URL için dummy data tanımlıysa, API'ye gitme, direkt onu kullan
    const dummyData = getDummyData();
    if (dummyData.length > 0) {
      // Dummy data'daki id'leri string'e çevir
      const normalizedData = dummyData.map((row: any) => ({
        ...row,
        id: String(row.id || row.ID || ""),
      }));
      setData(normalizedData);
      setLoading(false);
      // selectedItem varsa seçili olarak işaretle
      if (selectedItem) {
        if (Array.isArray(selectedItem)) {
          // Çoklu seçim
          const itemIds = selectedItem
            .map((item: any) => String(item?.id || item?.ID || ""))
            .filter((id: string) => id && normalizedData.some((row) => String(row.id) === id));
          if (itemIds.length > 0) {
            setSelectionModel(itemIds);
            latestSelectionRef.current = itemIds;
          }
        } else {
          // Tek seçim
          const valueItem: any = selectedItem;
          let itemId = String(valueItem?.id || valueItem?.ID || "");

          // Önce id ile eşleşmeyi dene
          let targetId = "";
          if (itemId && normalizedData.some((row) => String(row.id) === itemId)) {
            targetId = itemId;
          } else {
            // id eşleşmezse, isim ile row bul
            const displayValue =
              valueItem?.[displayField] ||
              valueItem?.name ||
              valueItem?.NAME ||
              valueItem?.Name ||
              valueItem?.displayName ||
              "";

            if (displayValue) {
              const foundRow = normalizedData.find((row) => {
                const rowDisplay =
                  row?.[displayField] ||
                  row?.name ||
                  row?.NAME ||
                  row?.Name ||
                  row?.displayName ||
                  "";
                return rowDisplay === displayValue;
              });

              if (foundRow) {
                targetId = String(foundRow.id);
              }
            }
          }

          if (targetId) {
            setSelectionModel([targetId]);
            latestSelectionRef.current = [targetId];
          }
        }
      }
      return;
    }

    setLoading(true);
    const fetchData = async () => {
      try {
        // Eğer staticData verildiyse API çağrısı yapmadan bunu kullan
        if (staticData && Array.isArray(staticData)) {
          const result = staticData.map((row: any) => ({
            ...row,
            id: String(row.id ?? row.ID ?? row.value ?? row.VALUE),
          }));
          setData(result);
          return;
        }

        // staticData yoksa ve url verilmemişse hiçbir şey yapma
        if (!url) {
          setData([]);
          return;
        }

        let res: MutationResponse | undefined;
        if (method === "GET") {
          res = await baseService.getAll<MutationResponse>(url);
        } else {
          res = await baseService.add<MutationResponse>(url, postData);
        }
        // PATCH: result.data desteği
        let rawList: any[] = [];
        if (res?.DbResponse?.Result) {
          rawList = res.DbResponse.Result;
        } else if ((res as any)?.result?.data) {
          rawList = (res as any).result.data;
        } else if ((res as any)?.data) {
          rawList = (res as any).data;
        }
        const result = (rawList ?? []).map((row: any) => ({
          ...row,
          id: String(row.ID ?? row.id),
        }));
        setData(result);
        // selectedItem varsa seçili olarak işaretle
        if (selectedItem) {
          if (Array.isArray(selectedItem)) {
            // Çoklu seçim
            const itemIds = selectedItem
              .map((item: any) => String(item?.id || item?.ID || ""))
              .filter((id: string) => id && result.some((row) => String(row.id) === id));
            if (itemIds.length > 0) {
              setSelectionModel(itemIds);
              latestSelectionRef.current = itemIds;
            }
          } else {
            // Tek seçim
            const valueItem: any = selectedItem;
            let itemId = String(valueItem?.id || valueItem?.ID || "");

            // Önce id ile eşleşmeyi dene
            let targetId = "";
            if (itemId && result.some((row) => String(row.id) === itemId)) {
              targetId = itemId;
            } else {
              // id eşleşmezse, isim ile row bul
              const displayValue =
                valueItem?.[displayField] ||
                valueItem?.name ||
                valueItem?.NAME ||
                valueItem?.Name ||
                valueItem?.displayName ||
                "";

              if (displayValue) {
                const foundRow = result.find((row) => {
                  const rowDisplay =
                    row?.[displayField] ||
                    row?.name ||
                    row?.NAME ||
                    row?.Name ||
                    row?.displayName ||
                    "";
                  return rowDisplay === displayValue;
                });

                if (foundRow) {
                  targetId = String(foundRow.id);
                }
              }
            }

            if (targetId) {
              setSelectionModel([targetId]);
              latestSelectionRef.current = [targetId];
            }
          }
        }
      } catch (err) {
        console.error("Veriyi çekerken hata oluştu:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, url, method, postData, staticData]);

  // selectedItem değiştiğinde ve data yüklendiğinde seçimi güncelle
  useEffect(() => {
    if (selectedItem && data.length > 0) {
      if (Array.isArray(selectedItem)) {
        // Çoklu seçim
        const itemIds = selectedItem
          .map((item: any) => String(item?.id || item?.ID || ""))
          .filter((id: string) => id && data.some((row) => String(row.id) === id));
        if (itemIds.length > 0) {
          setSelectionModel(itemIds);
          latestSelectionRef.current = itemIds;
        }
      } else {
        // Tek seçim
        const valueItem: any = selectedItem;
        let itemId = String(valueItem?.id || valueItem?.ID || "");

        // Önce id ile eşleşmeyi dene
        let targetId = "";
        if (itemId && data.some((row) => String(row.id) === itemId)) {
          targetId = itemId;
        } else {
          // id eşleşmezse, isim ile row bul
          const displayValue =
            valueItem?.[displayField] ||
            valueItem?.name ||
            valueItem?.NAME ||
            valueItem?.Name ||
            valueItem?.displayName ||
            "";

          if (displayValue) {
            const foundRow = data.find((row) => {
              const rowDisplay =
                row?.[displayField] ||
                row?.name ||
                row?.NAME ||
                row?.Name ||
                row?.displayName ||
                "";
              return rowDisplay === displayValue;
            });

            if (foundRow) {
              targetId = String(foundRow.id);
            }
          }
        }

        if (targetId) {
          setSelectionModel([targetId]);
          latestSelectionRef.current = [targetId];
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, data.length]);

  const handleDialogOkClickInternal = () => {
    const selectedIds = latestSelectionRef.current;
    const selectedIdSet = new Set(selectedIds);
    const selectedRowsData = data.filter(row =>
      selectedIdSet.has(row.id)
    );
    const selectedItem = singleSelection ? (selectedRowsData.length > 0 ? selectedRowsData[0] : null) : selectedRowsData;
    
    // Önce inputValue'yu güncelle (hemen görünsün)
    if (selectedItem) {
      if (Array.isArray(selectedItem)) {
        const displayValues = selectedItem
          .map((item: any) => {
            // Tüm olası field adlarını kontrol et
            return item?.[displayField] || item?.name || item?.NAME || item?.Name || item?.displayName || "";
          })
          .filter((val: string) => val);
        if (displayValues.length > 0) {
          setInputValue(displayValues.join(", "));
        } else {
          setInputValue("");
        }
      } else {
        // Tüm olası field adlarını kontrol et
        const displayValue = selectedItem?.[displayField] || selectedItem?.name || selectedItem?.NAME || selectedItem?.Name || selectedItem?.displayName || "";
        setInputValue(displayValue);
      }
    } else {
      setInputValue("");
    }
    
    // Sonra onChange'i çağır (parent component state'ini günceller)
    onChange?.(selectedItem);
    onDialogOkClick?.();
    handleClose();
  };

  // Placeholder için boş değer kontrolü
  const showPlaceholder = !inputValue || inputValue === "";
  const placeholderText = showPlaceholder ? (rest.placeholder || t("buttons.chose")) : undefined;

  return (
    <>
      <ByzMuiTextField
        {...rest}
        value={inputValue}
        label={label || t("buttons.chose")}
        placeholder={placeholderText}
        variant="outlined"
        color="primary"
        inputProps={{
          readOnly: true,
          style: { cursor: rest.disabled ? "not-allowed" : "pointer" },
          placeholder: "",
        }}
        onClick={handleOpen}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen();
                }}
                disabled={rest.disabled}
              >
                <MoreHorizIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          width: rest.fullWidth ? "100%" : "300px",
          height: "36px",
          borderColor: "#2196F3",
          ...(rest.disabled && {
            backgroundColor: "#f5f5f5",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#e0e0e0",
            },
            "& input": {
              color: "#9e9e9e",
            },
            "& label": {
              color: "#9e9e9e",
            },
          }),
          ...rest.sx,
        }}
      />

      <OpenComponentDialog
        title={dialogTitle || t("buttons.chose")}
        open={isOpen}
        showOkButton={dialogShowOkButton}
        onSaveClick={handleDialogOkClickInternal}
        onClose={handleClose}
        maxWidth="md"
        fullWidth={false}
        rowID={rowID}
        showPatientData={showPatientData ?? dialogShowPatientData ?? Boolean(rowID)}
        sx={
          dialogMaxHeight
            ? {
                ".MuiPaper-root": {
                  maxHeight: dialogMaxHeight,
                },
              }
            : undefined
        }
      >
        <Box sx={{ minHeight: 400, display: "flex", flexDirection: "column" }}>
          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ByzMuiDataGrid
              apiRef={apiRef}
              rows={data}
              columns={dataColumns}
              checkboxSelection={!singleSelection}
              disableMultipleRowSelection={singleSelection}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={handleSelectionChange}
              sx={{
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#2196F3 !important",
                  "&:hover": {
                    backgroundColor: "#1976d2 !important",
                  },
                  "& .MuiDataGrid-cell": {
                    color: "#fff",
                  },
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                },
              }}
            />
          )}
        </Box>
      </OpenComponentDialog>
    </>
  );
};

export default ByzSelectListPopupInput;